import React, {FC, ReactElement, useContext, useEffect, useRef, useState} from 'react';
import PageTitle from '../components/PageTitle.component';
import TextInputComponent from '../components/form-components/TextInput.component';
import PageContentWrapper from '../components/PageContentWrapper.component';
import {TextInputType} from '../@types/text-input-types';
import FormWrapper from '../components/form-components/FormWrapper';
import ButtonComponent from '../components/form-components/Button.component';
import {UserContext} from '../context/userContext';
import {useNavigate} from 'react-router-dom';
import AuthService from '../services/auth.service';
import ErrorAlertComponent from '../components/ErrorAlert.component';
import {AxiosError} from 'axios';
import {BASE_PATH} from '../config/constants';
import OktaAuth from '@okta/okta-auth-js';

const Signin: FC<any> = (): ReactElement => {

    const { signinUser, oktaAuthOptions } = useContext(UserContext);
    const navigate = useNavigate();

    const [username, setUsername] = useState<string>();
    const [password, setPassword] = useState<string>();
    const [error, setError] = useState<string|null>(null);

    // Use useRef to ensure OAuth flow is only initiated once
    // This survives re-renders and is the most reliable way to prevent duplicates
    const oktaFlowInitiated = useRef(false);

    // Occurs ONCE, AFTER the initial render.
    useEffect(() => {

        // If the user is already logged in, redirect them to the home page.
        const currentUser = AuthService.getCurrentUser();

        if (currentUser) {
            // Check if there's a redirect URL stored
            const redirectUrl = sessionStorage.getItem('redirectUrl');
            if (redirectUrl) {
                sessionStorage.removeItem('redirectUrl');
                navigate(redirectUrl);
            } else {
                navigate('/react/workspace');
            }
            return;
        }

        // Check if we should automatically redirect to Okta
        // Use ref to ensure this only happens once, even with re-renders

        if (oktaFlowInitiated.current) {
            return; // Already initiated, don't do it again
        }

        AuthService.fetchSystemInfo().then((response) => {

            // Double-check the ref inside the async callback
            if (oktaFlowInitiated.current) {
                return;
            }

            if (response.data.eimMode && oktaAuthOptions) {

                // Set the flag BEFORE initiating the OAuth flow
                oktaFlowInitiated.current = true;

                localStorage.setItem('eimMode', 'true');


                const oktaAuth = new OktaAuth(oktaAuthOptions);
                oktaAuth.setOriginalUri('/isam/react/workspace');



                // Initiate the OAuth redirect
                oktaAuth.token.getWithRedirect().then(() => {
                    // OAuth flow initiated successfully, browser will redirect
                }).catch((e: any) => {
                    setError('Unable to retrieve tokens from Okta. Please try again. ' + e);
                    // On error, reset the flag to allow retry
                    oktaFlowInitiated.current = false;
                });
            }
            // If not in EIM mode, show traditional signin form
        }).catch(() => {
            // Silently ignore errors fetching system info, show traditional signin form
        });
    }, [oktaAuthOptions, navigate]);

    function handleSubmit(e: React.FormEvent): void {
        e.preventDefault();
        if (username && password) {
            setError(null);
            AuthService.signin(username, password)
                .then((response) => {
                    signinUser(response.data);
                    // Always redirect to workspace after successful authentication
                    sessionStorage.removeItem('redirectUrl'); // Clear any stored redirect
                    navigate('/react/workspace');
                })
                .catch((err: AxiosError) => {
                    if (err.response && err.response.status === 401) {
                        setError('Invalid username or password. Please try again.');
                    } else {
                        setError('An unexpected error occurred. Please try again later.');
                    }
                });
        } else {
            setError('Username and password are required');
        }
    }


    return (
        <>
            <PageTitle title={'Sign in'} />

            <PageContentWrapper>
                <FormWrapper width={400} align={'center'} onSubmit={handleSubmit}>
                    <TextInputComponent id={'username'} label={'Username'} type={TextInputType.email} value={username}
                        onChange={setUsername} required={true} />
                    <TextInputComponent id={'password'} label={'Password'} type={TextInputType.password}
                        value={password} onChange={setPassword} required={true} />
                    <div style={{margin: '5px 5px 5px', padding: 5, paddingTop: 0}}>
                        <span className='devkit-nav-link-nosub'>Forgot password? &nbsp;
                            <a className='devkit-main-menu-link' href={BASE_PATH + '/forgot-password'}>
                                Reset Password
                            </a>
                        </span>
                    </div>
                    <ButtonComponent id={'submit-login'} label={'Sign in'} size={'large'} buttonType={'submit'} />
                    {error ? <ErrorAlertComponent message={error} />  : null}
                </FormWrapper>
            </PageContentWrapper>
        </>
    );

};

export default Signin;
